export default {
  name: {
    id: 'rm_metal_lithium_name',
    defaultMessage: 'Lithium',
  },
  smartphoneLabel: {
    id: 'rm_metal_lithium_smartphone_label',
    defaultMessage: 'Significant',
  },
  whatItDoesText: {
    id: 'rm_metal_lithium_what_it_does',
    defaultMessage:
      'Lithium is used in lithium-ion batteries, storing and releasing the energy that powers your phone.',
  },
  extractionText1: {
    id: 'rm_metal_lithium_extraction_p1',
    defaultMessage:
      'Lithium is extracted through two very different methods. The first is hard-rock mining, in which deposits of spodumene (a lithium-rich mineral) are blasted, crushed, and chemically processed. This method is concentrated primarily in Australia.',
  },
  extractionText2: {
    id: 'rm_metal_lithium_extraction_p2',
    defaultMessage:
      'The second method involves pumping lithium-rich brines from underground salt flats, or salars, to the surface, where solar-evaporation ponds concentrate the lithium over several months before chemical processing. This method is concentrated in South America, especially Chile and Argentina.',
  },
  quickFactText: {
    id: 'rm_metal_lithium_quick_fact',
    defaultMessage:
      'The lithium in your phone may come from regions facing conflicts over water and Indigenous rights.',
  },
  envSummaryText: {
    id: 'rm_metal_lithium_env_summary',
    defaultMessage:
      'Lithium extraction is far from harmless and is shaped by one central challenge: water. Around half of global lithium production is concentrated in some of the world’s driest regions. In South America, brine extraction can consume water at a scale that directly threatens the ecosystems and communities that depend on it.',
  },
  envWaterText: {
    id: 'rm_metal_lithium_env_water_text',
    defaultMessage:
      'South American lithium is extracted from brines. These are vast underground reservoirs of salty water pumped to the surface and spread across open-air evaporation pools. The water loss is significant: between 3,500 and 28,300 cubic feet disappear per 2,200 lbs of lithium carbonate produced. Pumping may also disrupt underground water systems, potentially drawing freshwater into brine aquifers and reducing its availability for ecosystems and communities. This is particularly severe in the Lithium Triangle (Chile, Argentina, Bolivia), home to over half of the global lithium reserve and some of the driest landscapes on Earth.',
  },
  envBiodiversityText: {
    id: 'rm_metal_lithium_env_biodiversity_text',
    defaultMessage:
      'Lithium mines destroy habitats by opening up previously remote areas to roads, settlements, agriculture and poaching. If current trends continue then biodiversity threats from lithium mining could increase by 500 to 900% by 2050. This is driven by expanding extraction zones and new mines increasingly located in regions with weak environmental regulations... but high ecological value.',
  },
  envClimateText: {
    id: 'rm_metal_lithium_env_climate_text',
    defaultMessage:
      'Turning raw lithium into a usable material requires large quantities of industrial chemicals, including acids and nitrogen compounds, whose production consumes significant fossil fuels. Lithium’s climate footprint varies by extraction route, resource grade, processing and electricity mix. Globally, mineral and metal production accounts for about 10% of total energy-related greenhouse gas emissions.',
  },
  humanSummaryText: {
    id: 'rm_metal_lithium_human_summary',
    defaultMessage:
      'Lithium is the backbone of the clean energy transition, powering every electric vehicle and rechargeable battery. But its extraction carries significant human costs. Concentrated in the Lithium Triangle (Chile, Argentina, Bolivia), which holds over half of global reserve, the communities living in the area where the deposits are found often bear the brunt of industrial expansion, with conflicts over water, land, and livelihoods increasingly documented across the region.',
  },
  humanDisplacementText: {
    id: 'rm_metal_lithium_human_displacement_text',
    defaultMessage:
      'The Atacameño, Colla and Aymara Indigenous communities in Chile and Argentina have formally objected to lithium extraction on their ancestral salt flats, citing inadequate consultation and the failure to obtain free, prior and informed consent (FPIC). Chile has ratified ILO Convention 169, which requires consultation with Indigenous communities. The UN Declaration on the Rights of Indigenous Peoples recognises their right to FPIC. Communities have pursued national and international legal action to challenge extraction, yet projects have continued. A similar concern has emerged in Nevada, United States, where Amnesty reports that three lithium projects are moving forward without FPIC from affected Indigenous Peoples, threatening their culture, health, water and environment. These cases illustrate the gap that can arise between formal rights and their implementation.',
  },
  humanWaterText: {
    id: 'rm_metal_lithium_human_water_text',
    defaultMessage:
      'About half of global lithium production is concentrated in areas of high water stress. Extracting lithium from continental brines loses 100 to 800 cubic metres of water per tonne of lithium carbonate produced through open-air evaporation, causing freshwater to seep into brine aquifers and contaminating groundwater. For the Atacameño, Colla and Aymara communities in South America, this directly threatens both drinking water and llama herding, a practice that is culturally central to their way of life.',
  },
  humanLegalText: {
    id: 'rm_metal_lithium_human_legal_text',
    defaultMessage:
      'Many countries have legal and environmental review requirements for lithium mining, alongside voluntary corporate standards. Enforcement may be weak where investment is prioritised. In Chile, land and water disputes reflect concerns about inadequate consultation. Chile’s National Lithium Strategy seeks to embed community interests through state-controlled joint ventures, but implementation remains uneven.',
  },
  geoSummaryText: {
    id: 'rm_metal_lithium_geo_summary',
    defaultMessage:
      'Digital technology relies on lithium more than any other base metal for batteries. Yet growing demand, price volatility, and slow supply expansion are putting increasing pressure on global supply chains. Unlike cobalt, no export restrictions are currently in force: the supply risk today is market-driven rather than politically imposed.',
  },
  geoDugText: {
    id: 'rm_metal_lithium_geo_dug_text',
    defaultMessage:
      'Lithium mining is more geographically distributed than most critical minerals. Australia leads with 36.7% of global production, and the top three producing countries together account for 74.2% of mining capacity.',
  },
  geoProcessedText: {
    id: 'rm_metal_lithium_geo_processed_text',
    defaultMessage:
      "Mining is one thing; refining is another. China processes 70% of the world's lithium, and the top three refining countries together control 96% of global refining capacity. That concentration creates a significant bottleneck: even if mining diversifies, the world's lithium still flows through a handful of processing facilities.",
  },
  geoLeverageText: {
    id: 'rm_metal_lithium_geo_leverage_text',
    defaultMessage: 'No major export restrictions documented.',
  },
  supplySummaryText: {
    id: 'rm_metal_lithium_supply_summary',
    defaultMessage:
      'Lithium is the most price-volatile strategic mineral on the planet and that instability is itself part of the problem. Its price multiplied by 8 between 2021 and 2022, then crashed by 80% between 2023 and 2024. Opening a new lithium mine takes years and costs billions. When prices collapse, investors pull back and projects get shelved. The world needs 55 new mines by 2035 to meet demand. At current investment rates, that target is out of reach.',
  },
  supplyReservesText: {
    id: 'rm_metal_lithium_supply_reserves_text',
    defaultMessage:
      'There is no shortage of lithium in the ground. The challenge is accessing it: most deposits require years of permitting, billions in investment, and complex extraction whether from hard rock mines or salt flats. In 2024, the world produced around 205,000 tonnes. That number needs to more than double by 2030. The reserves exist. The infrastructure to extract them fast enough does not.',
  },
  supplyDemandText: {
    id: 'rm_metal_lithium_supply_demand_text',
    defaultMessage:
      'A lot more than we produce today. Lithium demand from clean-energy technologies could increase by more than 300% between 2025 and 2050, reaching 1.12 million tonnes. EVs and battery storage would drive this growth, accounting for nearly 90% of demand in 2050. Compared with the base-case supply forecast, the outlook points to a potential 48% lithium shortfall by 2040.',
  },
  recyclingSummaryText: {
    id: 'rm_metal_lithium_recycling_summary',
    defaultMessage:
      'Lithium can be recovered from used batteries, and the technology to do so exists. But most electric vehicle batteries sold in the last decade are still in use, so very little material is available to recycle yet. A bigger problem: the most common type of car battery today is cheap to make but costly to recycle. Without rules forcing manufacturers to take responsibility, the batteries we produce the most risk becoming the ones we recover the least.',
  },
  recyclingInputText: {
    id: 'rm_metal_lithium_recycling_input_text',
    defaultMessage:
      'Today, only a small fraction of the lithium used in new batteries comes from recycled sources, around 3%, because the first generation of EV batteries has barely reached end of life. That share will grow as more batteries retire but the process is slow, and demand is not waiting.',
  },
  recyclingOutlookText: {
    id: 'rm_metal_lithium_recycling_outlook_text',
    defaultMessage:
      'No, not on its own. Even under optimistic projections, battery recycling could meet 20 to 30% of lithium demand by 2050. The remaining 70 to 80% will still need to come from newly mined material. Recycling is a necessary part of the solution. It is not a substitute for building new mines.',
  },
  mine0Region: {
    id: 'rm_metal_lithium_mine0_region',
    defaultMessage: 'Greenbushes',
  },
  mine1Region: {
    id: 'rm_metal_lithium_mine1_region',
    defaultMessage: 'Pilgangoora',
  },
  mine2Region: {
    id: 'rm_metal_lithium_mine2_region',
    defaultMessage: 'Salar de Atacama',
  },
  mine3Region: {
    id: 'rm_metal_lithium_mine3_region',
    defaultMessage: 'Salar de Olaroz',
  },
  mine4Region: {
    id: 'rm_metal_lithium_mine4_region',
    defaultMessage: 'Cornwall (mine project)',
  },
} as const
